/**
 * CKL Collection Desktop Phase 1 — toolbar helpers.
 * Loaded only on collection templates via snippets/usf.liquid.
 * Desktop behavior activates only at min-width: 1000px.
 *
 * Lifecycle notes (critical):
 * - Apply horizontal layout ONCE per desktop activation.
 * - Never call USF refresh/search from the MutationObserver.
 * - Observer only reacts to genuine USF childList replacement,
 *   ignores our own writes via a re-entry guard, and schedules
 *   at most one soft update per animation frame.
 */
(function () {
  var MQ = '(min-width: 1000px)';
  var BODY_CLASS = 'ckl-collection-desktop';
  var FACET_MAP = {
    product: 'product',
    color: 'color',
    profile: 'profile'
  };

  var mediaQuery = window.matchMedia(MQ);
  var observer = null;
  var observedRoot = null;
  var keysBound = false;
  var usfEventsBound = false;
  var desktopActive = false;
  var horzApplied = false;
  var activeRoot = null;
  var lastEscapeTrigger = null;
  var retryTimer = null;
  var retryTries = 0;
  var savedHorz = null;
  var hasSavedHorz = false;
  var mutatingSelf = false;
  var rafScheduled = false;
  var lastCountText = '';

  function isCollectionPath() {
    return /\/collections\//.test(location.pathname);
  }

  function isDesktop() {
    return mediaQuery.matches;
  }

  function titleFromCollectionHandle() {
    var match = location.pathname.match(/\/collections\/([^\/?#]+)/i);
    if (!match) return '';
    var handle = decodeURIComponent(match[1] || '');
    if (!handle || handle === 'all') return '';
    return handle.replace(/[-_]+/g, ' ').replace(/\b\w/g, function (ch) { return ch.toUpperCase(); });
  }

  function getFacetFilters() {
    try {
      if (window.usf && usf.query && usf.query.facetFilters) {
        return usf.query.facetFilters;
      }
    } catch (e) {}
    try {
      if (window.usf && usf.search && usf.search.facetFilters) {
        return usf.search.facetFilters;
      }
    } catch (e2) {}
    return null;
  }

  function setFacetFilters(next) {
    try {
      if (window.usf && usf.queryRewriter) {
        usf.queryRewriter.facetFilters = next && typeof next === 'object' ? next : {};
      }
    } catch (e) {}
  }

  function cloneFacetFilters(filters) {
    var next = {};
    if (!filters) return next;
    Object.keys(filters).forEach(function (key) {
      var entry = filters[key];
      if (!entry) return;
      next[key] = [entry[0], Array.isArray(entry[1]) ? entry[1].slice() : []];
    });
    return next;
  }

  function mobileFilterDotColor(value) {
    var colors = {
      white: '#f4f4f4', ivory: '#fff5df', yellow: '#ffea00', orange: '#ff8a00',
      pink: '#ff9eb5', red: '#f21b1b', brown: '#a64b16', purple: '#a500a5',
      green: '#0d7a18', blue: '#001fd1', denim: '#293b6f', grey: '#9b9b9b',
      gray: '#9b9b9b', black: '#000000'
    };
    return colors[String(value || '').toLowerCase().trim()] || '';
  }

  function removeMobileFacetValue(facetId, value) {
    var filters = cloneFacetFilters(getFacetFilters());
    var entry = filters[facetId];
    if (!entry) return;
    var index = entry[1].indexOf(value);
    if (index !== -1) entry[1].splice(index, 1);
    if (!entry[1].length) delete filters[facetId];
    setFacetFilters(filters);
  }

  function clearMobileFacetFilters() {
    try {
      if (window.usf && usf.queryRewriter && typeof usf.queryRewriter.removeAllFacetFilters === 'function') {
        usf.queryRewriter.removeAllFacetFilters();
        return;
      }
    } catch (e) {}
    setFacetFilters({});
  }

  function syncMobileSelectedFilters() {
    if (!isCollectionPath() || isDesktop()) return;
    var root = getRoot();
    if (!root) return;
    var config = root.querySelector('.usf-sr-config');
    var wrapper = root.querySelector('.usf-sr-config__mobile-filters-wrapper');
    var filterButton = wrapper && wrapper.querySelector('.usf-filters > .usf-btn');
    if (!config || !wrapper || !filterButton) return;

    var filters = getFacetFilters();
    var selected = [];

    if (filters) {
      Object.keys(filters).forEach(function (facetId) {
        var entry = filters[facetId];
        if (!entry || !Array.isArray(entry[1])) return;
        entry[1].forEach(function (value) {
          selected.push({
            collection: false,
            facetId: facetId,
            facetName: String(entry[0] || ''),
            value: value,
            label: String(value)
          });
        });
      });
    }

    var existingCount = filterButton.querySelector('.ckl-mobile-filter-count');
    if (selected.length) {
      if (!existingCount) {
        existingCount = document.createElement('span');
        existingCount.className = 'ckl-mobile-filter-count';
        filterButton.appendChild(existingCount);
      }
      existingCount.textContent = String(selected.length);
    } else if (existingCount) {
      existingCount.remove();
    }

    var summary = config.querySelector('.ckl-mobile-selected-filters');
    if (!selected.length) {
      if (summary) summary.remove();
      return;
    }

    if (!summary) {
      summary = document.createElement('div');
      summary.className = 'ckl-mobile-selected-filters';
      wrapper.insertAdjacentElement('afterend', summary);
    }

    summary.innerHTML = '';
    var chips = document.createElement('div');
    chips.className = 'ckl-mobile-selected-filters__chips';

    selected.forEach(function (item) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'ckl-mobile-filter-chip';

      var isColor = /color/i.test(item.facetName || '');
      var dotColor = isColor ? mobileFilterDotColor(item.label) : '';
      if (dotColor) {
        var dot = document.createElement('span');
        dot.className = 'ckl-mobile-filter-chip__dot';
        dot.style.backgroundColor = dotColor;
        chip.appendChild(dot);
      }

      var label = document.createElement('span');
      label.className = 'ckl-mobile-filter-chip__label';
      label.textContent = item.label;
      chip.appendChild(label);

      var remove = document.createElement('span');
      remove.className = 'ckl-mobile-filter-chip__remove';
      remove.setAttribute('aria-hidden', 'true');
      chip.appendChild(remove);

      chip.addEventListener('click', function () {
        removeMobileFacetValue(item.facetId, item.value);
      });
      chips.appendChild(chip);
    });

    if (selected.length > 1 || (filters && Object.keys(filters).length)) {
      var clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'ckl-mobile-filter-clear';
      clear.textContent = 'Clear all';
      clear.addEventListener('click', clearMobileFacetFilters);
      chips.appendChild(clear);
    }

    summary.appendChild(chips);
  }

  function normalizeFacetKey(title) {
    var key = String(title || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(/\s+/)[0];
    return FACET_MAP[key] || key || 'unknown';
  }

  function getRoot() {
    return document.getElementById('usf_container');
  }

  function getFacetButton(facet) {
    return facet.querySelector('.usf-title .usf-label.usf-btn, .usf-title button.usf-btn');
  }

  function withSelfMutation(fn) {
    mutatingSelf = true;
    try {
      fn();
    } finally {
      // Release after the current task so observer callbacks from our
      // own writes are ignored even when delivered asynchronously.
      window.setTimeout(function () {
        mutatingSelf = false;
      }, 0);
    }
  }

  function getVueVm(el) {
    if (!el) return null;
    return el.__vue__ || null;
  }

  /** Filters root VM owns `collapsed` map + horz accordion (USF onExpandCollapse). */
  function getFiltersVm(root) {
    if (!root) return null;
    var host =
      root.querySelector('.usf-facets') ||
      root.querySelector('.usf-sr-filters') ||
      root.querySelector('usf-filters');
    var vm = getVueVm(host);
    if (vm && vm.collapsed && vm.facets) return vm;
    // Walk up from a facet if the host node is a child of the filters VM.
    var facet = root.querySelector('.usf-facet');
    var cursor = facet;
    while (cursor && cursor !== root) {
      vm = getVueVm(cursor);
      if (vm && vm.collapsed && vm.facets) return vm;
      cursor = cursor.parentElement;
    }
    return null;
  }

  function syncExpanded(root) {
    if (!root) return;
    withSelfMutation(function () {
      root.querySelectorAll('.usf-facet').forEach(function (facet) {
        var btn = getFacetButton(facet);
        if (!btn) return;
        btn.setAttribute(
          'aria-expanded',
          facet.classList.contains('usf-collapsed') ? 'false' : 'true'
        );
      });
    });
  }

  function setFacetCollapsed(facet, collapsed) {
    if (!facet) return;
    var filtersVm = getFiltersVm(activeRoot || facet.closest('#usf_container'));
    var facetVm = getVueVm(facet);
    var facetId = facetVm && facetVm.id != null ? facetVm.id : null;

    if (filtersVm && facetId != null && typeof filtersVm.$set === 'function') {
      filtersVm.$set(filtersVm.collapsed, facetId, collapsed ? 1 : 0);
      syncExpanded(activeRoot || facet.closest('#usf_container'));
      return;
    }

    withSelfMutation(function () {
      if (collapsed) facet.classList.add('usf-collapsed');
      else facet.classList.remove('usf-collapsed');
      var btn = getFacetButton(facet);
      if (btn) btn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    });
  }

  function closeAllFacets(root, except) {
    if (!root) return;
    var filtersVm = getFiltersVm(root);
    if (filtersVm && filtersVm.facets && typeof filtersVm.$set === 'function') {
      var exceptId = null;
      if (except) {
        var exceptVm = getVueVm(except);
        if (exceptVm && exceptVm.id != null) exceptId = exceptVm.id;
      }
      filtersVm.facets.forEach(function (f) {
        if (exceptId != null && f.id === exceptId) return;
        filtersVm.$set(filtersVm.collapsed, f.id, 1);
      });
      syncExpanded(root);
      return;
    }
    root.querySelectorAll('.usf-facet').forEach(function (facet) {
      if (except && facet === except) return;
      if (!facet.classList.contains('usf-collapsed')) {
        setFacetCollapsed(facet, true);
      }
    });
  }

  /**
   * Do NOT stopPropagation — USF's Vue `@click="onExpandCollapse"` owns the
   * toggle + horz accordion via root.collapsed. We only record the trigger
   * for Escape focus return and refresh aria-expanded after Vue updates.
   */
  function onFacetTitleActivate(event) {
    if (!isDesktop() || !activeRoot) return;

    var title = event.currentTarget;
    var facet = title.closest('.usf-facet');
    if (!facet || !activeRoot.contains(facet)) return;

    if (event.target && event.target.closest && event.target.closest('.usf-facet > .usf-container')) {
      return;
    }

    lastEscapeTrigger = getFacetButton(facet) || title;
    window.setTimeout(function () {
      if (!isDesktop() || !activeRoot) return;
      syncExpanded(activeRoot);
    }, 0);
  }

  function enhanceFacets(root) {
    if (!root || !isDesktop()) return;

    withSelfMutation(function () {
      root.querySelectorAll('.usf-facet').forEach(function (facet, index) {
        var btn = getFacetButton(facet);
        var panel = facet.querySelector(':scope > .usf-container');
        if (!btn || !panel) return;

        var titleText = (btn.textContent || '').trim();
        var facetKey = normalizeFacetKey(titleText);
        if (facet.getAttribute('data-ckl-facet') !== facetKey) {
          facet.setAttribute('data-ckl-facet', facetKey);
        }
        if (titleText && facet.getAttribute('data-facet-title') !== titleText) {
          facet.setAttribute('data-facet-title', titleText);
        }

        if (!panel.id) panel.id = 'ckl-facet-panel-' + index;
        if (btn.getAttribute('aria-controls') !== panel.id) {
          btn.setAttribute('aria-controls', panel.id);
        }
        var expanded = facet.classList.contains('usf-collapsed') ? 'false' : 'true';
        if (btn.getAttribute('aria-expanded') !== expanded) {
          btn.setAttribute('aria-expanded', expanded);
        }

        var title = facet.querySelector('.usf-title');
        if (title && title.dataset.cklBound !== '1') {
          title.dataset.cklBound = '1';
          // Bubble phase so Vue's onExpandCollapse runs first (its listener is on the same node).
          title.addEventListener('click', onFacetTitleActivate, false);
        }
      });
    });
  }

  /** Apply horizontal layout once per desktop activation — never from the observer. */
  function applyHorzFiltersOnce() {
    if (horzApplied) return;
    horzApplied = true;

    withSelfMutation(function () {
      try {
        if (window.usf && usf.settings && usf.settings.filters) {
          if (!hasSavedHorz) {
            savedHorz = usf.settings.filters.horz;
            hasSavedHorz = true;
          }
          if (usf.settings.filters.horz !== true) {
            usf.settings.filters.horz = true;
          }
        }
      } catch (e) {}

      var root = getRoot();
      if (root && !root.classList.contains('usf-filters-horz')) {
        root.classList.add('usf-filters-horz');
      }
      document.body.classList.remove('usf-vert-layout');
      if (!document.body.classList.contains('usf-horz-layout')) {
        document.body.classList.add('usf-horz-layout');
      }
    });
  }

  function restoreMobileFilters() {
    withSelfMutation(function () {
      try {
        if (hasSavedHorz && window.usf && usf.settings && usf.settings.filters) {
          usf.settings.filters.horz = savedHorz;
        }
      } catch (e) {}

      var root = getRoot();
      if (root) root.classList.remove('usf-filters-horz');
      document.body.classList.remove('usf-horz-layout');
    });
    horzApplied = false;
  }

  function updateBodyClass() {
    var root = getRoot();
    var enable = isCollectionPath() && !!root && isDesktop();
    withSelfMutation(function () {
      document.body.classList.toggle(BODY_CLASS, enable);
      if (!enable) document.body.classList.remove('ckl-collection-all-loaded');
    });
    return enable;
  }

  function readUsfTotal(root) {
    var total = null;

    // Canonical USF search result (usf.utils.getResult does not exist).
    try {
      if (window.usf && usf.search && usf.search.result && usf.search.result.total != null) {
        total = Number(usf.search.result.total);
      }
    } catch (e) {}

    // Vue search-results VM on the SR container.
    if ((total == null || isNaN(total)) && root) {
      try {
        var sr = root.querySelector('.usf-sr-container');
        var srVm = getVueVm(sr);
        if (srVm && srVm.result && srVm.result.total != null) {
          total = Number(srVm.result.total);
        }
      } catch (e2) {}
    }

    if ((total == null || isNaN(total)) && root) {
      var progressTitle = root.querySelector('.usf-sr-more .usf-title');
      if (progressTitle) {
        var m = (progressTitle.textContent || '').match(/(\d+)\s*$/);
        if (m) total = Number(m[1]);
      }
    }

    if ((total == null || isNaN(total)) && root) {
      var existing = root.querySelector('.ckl-collection-count, .usf-sr-summary');
      if (existing) {
        var cm = (existing.textContent || '').match(/(\d+)/);
        if (cm) total = Number(cm[1]);
      }
    }

    return total != null && !isNaN(total) ? total : null;
  }

  function formatCount(total) {
    return total === 1 ? '1 product' : total + ' products';
  }

  function pruneSortOptions(root) {
    if (!root || !isDesktop()) return;

    var buttons = root.querySelectorAll('.usf-c-select__btn.usf-btn');
    buttons.forEach(function (btn) {
      var label = (btn.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      var remove =
        label.indexOf('percent sales:') === 0 ||
        label.indexOf('discount:') === 0;
      if (remove) {
        btn.style.setProperty('display', 'none', 'important');
        btn.setAttribute('aria-hidden', 'true');
        btn.setAttribute('tabindex', '-1');
      }
    });
  }

  function syncProductCount(root) {
    if (!root || !isDesktop()) return;

    var total = readUsfTotal(root);
    if (total == null) return;

    var config =
      root.querySelector('.usf-sr-container > .usf-sr-config') ||
      root.querySelector('.usf-sr-container .usf-sr-config');
    if (!config) return;

    var nextText = formatCount(total);
    var el =
      config.querySelector('.ckl-collection-count') ||
      config.querySelector('.usf-sr-summary');

    withSelfMutation(function () {
      if (!el) {
        el = document.createElement('span');
        el.className = 'ckl-collection-count usf-sr-summary';
        el.setAttribute('data-ckl-count', '1');
        config.insertBefore(el, config.firstChild);
      } else if (!el.classList.contains('ckl-collection-count')) {
        el.classList.add('ckl-collection-count');
      }

      if (el.textContent !== nextText) {
        el.textContent = nextText;
        lastCountText = nextText;
      }

      var loaded = root.querySelectorAll(
        '.usf-results .product-item, .usf-results product-item.product-item'
      ).length;
      var hasLoadMore = !!root.querySelector('.usf-load-more');
      var allLoaded = loaded > 0 && loaded >= total && !hasLoadMore;
      document.body.classList.toggle('ckl-collection-all-loaded', allLoaded);
    });
  }

  /** Soft update after USF replaces children — never re-applies horz / never searches. */
  function softUpdate(root) {
    if (!root || !isDesktop() || !desktopActive) return;
    enhanceFacets(root);
    syncProductCount(root);
    pruneSortOptions(root);
  }

  function scheduleSoftUpdate(root) {
    if (rafScheduled) return;
    rafScheduled = true;
    window.requestAnimationFrame(function () {
      rafScheduled = false;
      if (!desktopActive || !isDesktop()) return;
      var target = root || activeRoot || getRoot();
      if (target) softUpdate(target);
    });
  }

  function bindUsfEvents() {
    if (usfEventsBound) return;
    if (!window.usf || !usf.event || typeof usf.event.add !== 'function') return;
    usfEventsBound = true;
    usf.event.add(['sr_updated', 'sr_viewChanged', 'rerender', 'init'], function () {
      if (isDesktop()) {
        if (!desktopActive) return;
        scheduleSoftUpdate(getRoot());
      } else {
        window.requestAnimationFrame(syncMobileSelectedFilters);
      }
    });
  }

  function onKeydown(e) {
    if (!isDesktop() || !activeRoot) return;
    if (e.key !== 'Escape') return;

    var openFacet = activeRoot.querySelector('.usf-facet:not(.usf-collapsed)');
    var trigger =
      lastEscapeTrigger ||
      (openFacet ? getFacetButton(openFacet) : null);

    closeAllFacets(activeRoot);

    if (trigger && typeof trigger.focus === 'function') {
      window.setTimeout(function () {
        try {
          trigger.focus();
        } catch (err) {}
      }, 0);
    }
  }

  function onDocumentClick(e) {
    if (!isDesktop() || !activeRoot) return;
    if (activeRoot.contains(e.target)) {
      // Inside USF: only close when click is outside any open facet chrome
      // (not on a title — titles handle themselves — and not on filter values).
      var facet = e.target.closest && e.target.closest('.usf-facet');
      if (facet) return;
    }
    closeAllFacets(activeRoot);
  }

  function bindGlobalKeys() {
    if (keysBound) return;
    keysBound = true;
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('click', onDocumentClick);
  }

  function unbindGlobalKeys() {
    if (!keysBound) return;
    keysBound = false;
    document.removeEventListener('keydown', onKeydown);
    document.removeEventListener('click', onDocumentClick);
  }

  function stopObserver() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    observedRoot = null;
  }

  function startObserver(root) {
    if (!root) return;
    if (observer && observedRoot === root) return;

    stopObserver();
    observedRoot = root;

    observer = new MutationObserver(function (mutations) {
      if (mutatingSelf) return;
      if (!desktopActive || !isDesktop() || !activeRoot) return;

      var hasChildList = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'childList') {
          hasChildList = true;
          break;
        }
      }
      if (!hasChildList) return;

      // Genuine USF DOM replacement only — soft update, never horz/search.
      scheduleSoftUpdate(activeRoot);
    });

    // childList only: do not observe attributes we (or USF class toggles) write.
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: false
    });
  }

  function deactivateDesktop() {
    stopObserver();
    unbindGlobalKeys();
    restoreMobileFilters();
    withSelfMutation(function () {
      document.body.classList.remove(BODY_CLASS);
      document.body.classList.remove('ckl-collection-all-loaded');
    });
    desktopActive = false;
    activeRoot = null;
    lastEscapeTrigger = null;
    lastCountText = '';
    rafScheduled = false;
  }

  function activateDesktop() {
    if (!isCollectionPath()) {
      deactivateDesktop();
      return;
    }

    var root = getRoot();
    if (!root) {
      withSelfMutation(function () {
        document.body.classList.remove(BODY_CLASS);
      });
      return;
    }

    var alreadyActive = desktopActive && activeRoot === root;
    activeRoot = root;
    desktopActive = true;

    updateBodyClass();

    if (!alreadyActive) {
      applyHorzFiltersOnce();
      bindGlobalKeys();
      bindUsfEvents();
      startObserver(root);
    }

    softUpdate(root);
  }

  function syncMode() {
    if (!document.body || !isCollectionPath()) {
      deactivateDesktop();
      return;
    }

    if (isDesktop()) activateDesktop();
    else deactivateDesktop();
  }

  function onBreakpointChange() {
    syncMode();
  }

  function startRetries() {
    if (retryTimer) return;
    retryTries = 0;
    retryTimer = window.setInterval(function () {
      retryTries += 1;
      if (!isCollectionPath()) {
        window.clearInterval(retryTimer);
        retryTimer = null;
        return;
      }
      var root = getRoot();
      if (isDesktop()) {
        if (!desktopActive) {
          activateDesktop();
        } else if (root) {
          // Root appeared/updated after USF async boot — soft only.
          scheduleSoftUpdate(root);
        }
      } else if (root) {
        syncMobileSelectedFilters();
      }
      if (root && root.querySelector('.usf-facet, .usf-results .product-item, .usf-results product-item')) {
        window.clearInterval(retryTimer);
        retryTimer = null;
      }
      if (retryTries > 40) {
        window.clearInterval(retryTimer);
        retryTimer = null;
      }
    }, 250);
  }

  function boot() {
    if (!document.body || !isCollectionPath()) return;

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onBreakpointChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(onBreakpointChange);
    }

    syncMode();
    bindUsfEvents();
    syncMobileSelectedFilters();
    startRetries();
    window.addEventListener('resize', function () {
      if (desktopActive && activeRoot && isDesktop()) {
        scheduleSoftUpdate(activeRoot);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
